import { Badge } from 'primereact/badge'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Editor } from 'primereact/editor'
import { InputTextarea } from 'primereact/inputtextarea'
import { ProgressSpinner } from 'primereact/progressspinner'
import { SetStateAction, useEffect, useRef, useState } from 'react'
import { authProvider } from '../../../../../providers/authProvider'
import { AddRemarksModel } from '../../../../../shared/models/service-models/AddRemarks.model'
import { RemarksHistoryModel } from '../../../../../shared/models/service-models/RemarksHistoryModel'
import classes from './RemarksHistoryDialog.module.scss'
import RemarksTypesEnum from './RemarksTypeEnum'

type Props = {
  e2eProjectLineId: string
  header: string
  isOpen: boolean
  onAdd: (reuestModel: any) => Promise<void>
  isModalLoading: boolean
  remarkType: RemarksTypesEnum
  list: RemarksHistoryModel[]
  closeDialog: () => void
  disableAddButtonWhenCommentIsEmpty?: boolean
  isAddDisabled?: boolean
}

export default function RemarksHistoryDialog({
  e2eProjectLineId,
  header,
  isOpen,
  onAdd,
  isModalLoading,
  remarkType,
  list,
  closeDialog,
  disableAddButtonWhenCommentIsEmpty = true,
  isAddDisabled = false,
}: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [inputPlaceHolder, setInputPlaceHolder] = useState('')
  const [remarks, setRemarks] = useState('')
  const [remarksCount, setRemarksCount] = useState(0)
  const [isMaxRemarks, setIsMaxRemarks] = useState(false)

  useEffect(() => {
    setIsLoading(isModalLoading)
  }, [isModalLoading])

  useEffect(() => {
    setRemarks('')
    setInputPlaceHolder(`Add a ${header.toLowerCase()}`)
  }, [isOpen])

  useEffect(() => {
    setIsMaxRemarks(remarks?.length === 250 || remarksCount >= 250)
  }, [remarks])

  const onHide = () => {
    closeDialog()
  }

  const footer = (
    <div className={classes['footer']}>
      <div>
        <Button className={`${classes['button-cancel']}`} label="CLOSE" onClick={onHide} />
      </div>
    </div>
  )

  const dialogStyle = {
    width: '64rem',
  }

  const userInfo = authProvider.getAccountInfo()

  const renderEditorHeader = (
    <>
      <span className="ql-formats">
        <button className="ql-bold" aria-label="Bold"></button>
        <button className="ql-italic" aria-label="Italic"></button>
        <button className="ql-underline" aria-label="Underline"></button>
      </span>

      <span className="ql-formats">
        <button className="ql-align" value=""></button>
        <button className="ql-align" value="center"></button>
        <button className="ql-align" value="right"></button>
      </span>

      <span className="ql-formats">
        <button className="ql-list" value="ordered"></button>
        <button className="ql-list" value="bullet"></button>

        <button className="ql-indent" value="-1"></button>
        <button className="ql-indent" value="+1"></button>
      </span>

      <span className="ql-formats">
        <select className="ql-color"></select>
        <select className="ql-background"></select>
      </span>

      {/* FONT size */}
      <span className="ql-formats">
        <select className="ql-size">
          <option value="12px">8pt</option>
          <option value="14px">10pt</option>
          <option value="16px">12pt</option>
          <option value="18px">14pt</option>
          <option value="24px">18pt</option>
          <option value="32px">24pt</option>
          <option value="48px">36pt</option>
        </select>
      </span>

      <span className="ql-formats">
        <select className="ql-font" style={{ width: '150px' }}>
          <option value="arial">Arial</option>
          <option value="courier-new">Courier New</option>
          <option value="georgia">Georgia</option>
          <option value="impact">Impact</option>
          <option value="lucida-console">Lucida Console</option>
          <option value="tahoma">Tahoma</option>
          <option value="times-new-roman">Times New Roman</option>
          <option value="trebuchet-ms">Trebuchet MS</option>
          <option value="verdana">Verdana</option>
        </select>
      </span>
    </>
  )

  const renderEditorBody = (
    <Editor
      style={{ height: '250px' }}
      headerTemplate={renderEditorHeader}
      value={remarks}
      onTextChange={(e: any) => handleOnTextChangeEditor(e)}
      className={classes['dialog-editor']}
    />
  )

  const handleOnTextChangeEditor = (e: any) => {
    const plainText = e?.textValue || 0

    setRemarksCount(plainText?.length - 1)
    setRemarks(e.htmlValue)
  }

  const handleAdd = async () => {
    setIsAdding(true)
    const payload: AddRemarksModel = {
      displayName: userInfo?.account.name,
      userEmail: userInfo?.account.idToken.email,
      projectLineRemarksType: remarkType,
      e2EProjectLineId: e2eProjectLineId,
      value: remarks,
    }
    await onAdd(payload)

    setRemarks('')
    setIsAdding(false)
  }

  const getInitials = (name: string) => {
    if (name == 'php.coesupport@ul.com' || name == 'PHP COE Support') return 'TC'

    return name
      ?.split(',')
      ?.map(value => value.trim())
      ?.reverse()
      ?.map((value, index, array) =>
        index === 0 || index + 1 === array.length ? (value[0] ?? '').toUpperCase() : null
      )
      .join('')
  }

  const onChangeRemarks = (event: { target: { value: SetStateAction<string> } }) => {
    setRemarks(event.target.value)
  }

  return (
    <Dialog
      draggable={false}
      resizable={false}
      className={classes['dialog']}
      header={header}
      footer={footer}
      visible={isOpen}
      style={dialogStyle}
      modal
      onHide={onHide}
      contentClassName={classes['content']}
    >
      {isLoading && (
        <div className={`${classes['spinner__container']}`}>
          <ProgressSpinner
            strokeWidth="5"
            animationDuration="5s"
            className={`${classes['spinner']}`}
          />
        </div>
      )}

      {!isLoading && (
        <div className={classes['container']}>
          {!isAddDisabled && (
            <>
              {remarkType === RemarksTypesEnum.TaskComment ? (
                renderEditorBody
              ) : (
                <InputTextarea
                  disabled={isLoading || isAdding || isAddDisabled}
                  className={classes['remarks-text-area']}
                  value={remarks}
                  onChange={onChangeRemarks}
                  placeholder={inputPlaceHolder}
                  maxLength={250}
                />
              )}

              <div className={classes['button-area']}>
                <div>
                  <small>
                    {remarkType === RemarksTypesEnum.TaskComment ? remarksCount : remarks?.length} /
                    250
                    {isMaxRemarks && (
                      <span className={classes['text-error']}>
                        {' '}
                        You have reached the allowed maximum characters.
                      </span>
                    )}
                    {remarks && remarks?.trim().length == 0 && (
                      <span className={classes['text-error']}>
                        {' '}
                        Please type a message to continue.
                      </span>
                    )}
                  </small>
                </div>

                <Button
                  loading={isAdding}
                  className={`${classes['button-add']}`}
                  label="ADD"
                  onClick={handleAdd}
                  disabled={
                    isLoading ||
                    (disableAddButtonWhenCommentIsEmpty &&
                      (!remarks || remarks?.trim().length == 0)) ||
                    isAdding ||
                    isAddDisabled ||
                    remarksCount > 250
                  }
                />
              </div>
            </>
          )}
          <h3> {header} History</h3>
          <div className={classes['value-container-outer']}>
            <div className={classes['value-container-wrapper']}>
              {list.map(item => {
                return (
                  <>
                    <div className={classes['value-container']}>
                      <div className={classes['value-header']}>
                        <Badge
                          value={getInitials(item.displayName)}
                          className={classes['badge']}
                          size="large"
                        />
                        <p>
                          {item.user} - {item.dateTimeDisplay}
                        </p>
                      </div>
                      <div
                        className={classes['value-content']}
                        dangerouslySetInnerHTML={{ __html: item.value }}
                      ></div>
                    </div>
                  </>
                )
              })}
            </div>

            <div className={classes['value-container-no-comment']}>
              {list.length === 0 && <p> No comments found. </p>}
            </div>
          </div>
        </div>
      )}
    </Dialog>
  )
}
